import { z } from 'zod'

// Contact form validation schema
export const contactFormSchema = z.object({
  name: z.string().min(1, 'Naam is verplicht').max(100, 'Naam is te lang'),
  email: z.string().email('Ongeldig e-mailadres'),
  message: z
    .string()
    .min(10, 'Bericht moet minimaal 10 karakters bevatten')
    .max(1000, 'Bericht is te lang'),
})

export type ContactFormData = z.infer<typeof contactFormSchema>

// User profile validation schema
export const userProfileSchema = z.object({
  name: z.string().min(1, 'Naam is verplicht').max(100, 'Naam is te lang'),
  email: z.string().email('Ongeldig e-mailadres'),
  department: z.string().optional(),
  phone: z.string().optional(),
})

export type UserProfileData = z.infer<typeof userProfileSchema>

// Profile update validation schema (matches backend UserUpdateSchema)
export const profileUpdateSchema = z.object({
  first_name: z.string().min(1, 'Voornaam is verplicht').max(100, 'Voornaam is te lang'),
  last_name: z.string().min(1, 'Achternaam is verplicht').max(100, 'Achternaam is te lang'),
  display_name: z.string().max(200, 'Weergavenaam is te lang').optional().or(z.literal('')),
  language: z.string().max(10, 'Taalcode is te lang'),
  timezone: z.string().max(50, 'Tijdzone is te lang'),
})

export type ProfileUpdateData = z.infer<typeof profileUpdateSchema>

// Password change validation schema
export const passwordChangeSchema = z
  .object({
    current_password: z.string().min(1, 'Huidig wachtwoord is verplicht'),
    new_password: z.string().min(6, 'Nieuw wachtwoord moet minimaal 6 karakters bevatten'),
    confirm_password: z.string().min(1, 'Wachtwoord bevestiging is verplicht'),
  })
  .refine(data => data.new_password === data.confirm_password, {
    message: 'Wachtwoorden komen niet overeen',
    path: ['confirm_password'],
  })

export type PasswordChangeData = z.infer<typeof passwordChangeSchema>

// Environmental scan validation schemas
export const scanFormSchema = z.object({
  keywords: z
    .array(z.string().min(1, 'Trefwoord mag niet leeg zijn'))
    .min(1, 'Minimaal één trefwoord is verplicht')
    .max(10, 'Maximaal 10 trefwoorden toegestaan'),
  timeFrame: z
    .object({
      preset: z.enum([
        'last_15_minutes',
        'last_30_minutes',
        'last_45_minutes',
        'last_1_hour',
        'last_24_hours',
        'custom',
      ]),
      from: z.date().optional(),
      to: z.date().optional(),
      fromDate: z.string().optional(),
      fromTime: z.string().optional(),
      toDate: z.string().optional(),
      toTime: z.string().optional(),
    })
    .refine(
      data => {
        if (data.preset === 'custom') {
          // Allow either the combined dates or separate date/time fields
          const hasCombined = data.from && data.to
          const hasSeparate = data.fromDate && data.fromTime && data.toDate && data.toTime
          if (hasCombined) {
            return data.from! < data.to!
          }
          if (hasSeparate) {
            // Parse dd/MM/yyyy format to create valid Date objects
            const [fromDay, fromMonth, fromYear] = data.fromDate!.split('/')
            const [toDay, toMonth, toYear] = data.toDate!.split('/')
            const fromDateTime = new Date(`${fromYear}-${fromMonth}-${fromDay}T${data.fromTime}`)
            const toDateTime = new Date(`${toYear}-${toMonth}-${toDay}T${data.toTime}`)
            return fromDateTime < toDateTime
          }
          return false
        }
        return true
      },
      {
        message: 'Voor aangepaste tijdperiode zijn begin- en einddatum verplicht',
      }
    ),
})

export type ScanFormData = z.infer<typeof scanFormSchema>

export const scanFiltersSchema = z.object({
  search: z.string().max(200, 'Zoekopdracht is te lang'),
  status: z.enum(['draft', 'processing', 'ready', 'confirmed']).optional(),
  timeFrame: z.enum([
    'last_15_minutes',
    'last_30_minutes',
    'last_45_minutes',
    'last_1_hour',
    'last_24_hours',
    'custom',
  ]),
  customFrom: z.date().optional(),
  customTo: z.date().optional(),
})

export type ScanFiltersData = z.infer<typeof scanFiltersSchema>

// Generic validation helpers
export const sanitizeString = (input: string): string => {
  return input.trim().replace(/[<>]/g, '')
}

export const validateRequired = (value: unknown, fieldName: string): string | undefined => {
  if (!value || (typeof value === 'string' && value.trim() === '')) {
    return `${fieldName} is verplicht`
  }
  return undefined
}
